<template>
    <app-layout>
        <template #header></template>

        <div v-if="categoryModalVisible">
            <ManageCateoryModal v-on:updateVisible="closeCategoryModal" />
        </div>

        <div class="py-12">
            <div class="max-w-7xl mx-auto px-1 sm:px-6 lg:px-8">
                <h1 class="text-2xl font-bold">
                    Edit asset information
                    <span v-if="asset.deleted_at" class="text-sm text-red-400"
                        >DELETED</span
                    >
                </h1>
                <div class="mb-4">
                    All mandatory fields* must be filled before publishing an
                    asset
                </div>

                <div v-if="form.type === 'photos'" class="mb-6">
                    <div class="block font-bold">Asset preview</div>
                    <div class="flex items-center">
                        <img class="w-full lg:w-1/2" :src="previewImageUrl" />
                        <div class="mx-2">
                            <label
                                for="assetsFieldHandle"
                                class="block cursor-pointer underline"
                            >
                                Replace file
                            </label>

                            <input
                                type="file"
                                name="fields[assetsFieldHandle][]"
                                id="assetsFieldHandle"
                                class="
                                    w-px
                                    h-px
                                    opacity-0
                                    overflow-hidden
                                    absolute
                                "
                                ref="file"
                                accept=".jpg,.jpeg,.ai"
                                @change="onFileReplace"
                            />

                            <div
                                v-if="imageErrors.images"
                                class="mt-1 text-sm text-red-400"
                            >
                                {{ imageErrors.images[0] }}
                            </div>
                        </div>
                    </div>
                    <div v-if="false" class="flex space-x-4 mt-1">
                        <a
                            :key="index"
                            v-for="(image, index) in asset.images.data"
                            class="underline"
                            :href="image.file_url"
                        >
                            {{ image.size_type.toUpperCase() }}
                        </a>
                    </div>
                </div>

                <div v-if="form.type === 'illustrations'" class="mb-6">
                    <div class="block font-bold">Asset preview</div>
                    <div class="text-gray-700 text-sm">
                        *Illustrations has no preview.
                    </div>
                </div>

                <FormTextLabel
                    v-model="form.title"
                    id="title"
                    label="Title *"
                    subtitle="Add a title that describes this item."
                    placeholderText="Add a title"
                    containerClass="mb-6"
                    :error="errors.title ? errors.title[0] : ''"
                />

                <FormTextAreaLabel
                    v-model="form.description"
                    id="description"
                    label="Description (optional)"
                    subtitle="Add details about this item."
                    placeholderText="Add a description"
                    containerClass="mb-6"
                    :error="errors.description ? errors.description[0] : ''"
                />

                <FormTextLabel
                    v-model="form.keywords"
                    id="keywords"
                    label="Keywords *"
                    subtitle="Include common keywords that users will use to search
                        for the asset. Separate different keywords using comma Eg. winding road, nature with roads,
                        indoors, mountains, trees, sand, etc."
                    placeholderText="Add keywords"
                    containerClass="mb-6"
                    :error="errors.keywords ? errors.keywords[0] : ''"
                />

                <div class="mb-6">
                    <label for="categories" class="block font-bold"
                        >Category *</label
                    >
                    <Multiselect
                        class="multiselect-gray mt-1"
                        :classes="{
                            container:
                                'relative mx-auto w-full flex items-center justify-end box-border cursor-pointer border border-gray-300 bg-white text-base leading-snug outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ',
                        }"
                        v-model="form.categories"
                        :object="true"
                        valueProp="id"
                        label="name"
                        :options="categoryList"
                        mode="tags"
                        :closeOnSelect="false"
                    />
                    <div
                        v-if="errors.categories"
                        class="mt-1 text-sm text-red-400"
                    >
                        {{ errors.categories[0] }}
                    </div>
                    <div class="flex items-center mt-1">
                        <button
                            class="underline"
                            @click="categoryModalShow(true)"
                        >
                            + Add and manage categories
                        </button>
                        <div class="text-green-600 text-sm ml-2">
                            {{ categoriesMessage }}
                        </div>
                    </div>
                </div>

                <div class="mb-6">
                    <div class="mb-2">
                        <div class="block font-bold">Asset owner(s) *</div>
                        <div class="text-sm text-gray-700 mt-1">
                            Provide contact details of asset owner(s) for any
                            usage rights enquiries.
                        </div>
                    </div>

                    <div
                        v-if="errors.asset_owners"
                        class="mt-1 text-sm text-red-400"
                    >
                        {{ errors.asset_owners[0] }}
                    </div>

                    <div
                        v-if="form.asset_owners && form.asset_owners.length > 0"
                    >
                        <div
                            class="mb-2"
                            v-for="(owner, index) in form.asset_owners"
                            :key="index"
                        >
                            <div class="font-bold text-sm">
                                Asset Owner {{ index + 1 }}
                            </div>

                            <div class="flex">
                                <div class="mr-2">
                                    <label :for="'owner-' + index + owner.email"
                                        >Email</label
                                    >

                                    <div class="mt-1">
                                        <input
                                            :id="'owner-' + index + owner.email"
                                            type="email"
                                            :name="
                                                'owner-' + index + owner.email
                                            "
                                            v-model="owner.email"
                                            class="
                                                shadow-sm
                                                focus:ring-indigo-500
                                                focus:border-indigo-500
                                                block
                                                w-full
                                                lg:w-72
                                                sm:text-sm
                                                border-gray-300
                                            "
                                        />
                                    </div>

                                    <div
                                        v-if="
                                            errors[
                                                `asset_owners.${index}.email`
                                            ]
                                        "
                                        class="mt-1 text-sm text-red-400"
                                    >
                                        {{
                                            errors[
                                                `asset_owners.${index}.email`
                                            ][0]
                                        }}
                                    </div>
                                </div>

                                <div class="mr-2">
                                    <label :for="'owner-' + index + owner.name"
                                        >Name</label
                                    >

                                    <div class="mt-1">
                                        <input
                                            :id="'owner-' + index + owner.name"
                                            type="text"
                                            :name="
                                                'owner-' + index + owner.name
                                            "
                                            v-model="owner.name"
                                            class="
                                                shadow-sm
                                                focus:ring-indigo-500
                                                focus:border-indigo-500
                                                block
                                                w-full
                                                lg:w-72
                                                sm:text-sm
                                                border-gray-300
                                            "
                                        />
                                    </div>

                                    <div
                                        v-if="
                                            errors[`asset_owners.${index}.name`]
                                        "
                                        class="mt-1 text-sm text-red-400"
                                    >
                                        {{
                                            errors[
                                                `asset_owners.${index}.name`
                                            ][0]
                                        }}
                                    </div>
                                </div>

                                <div class="mr-2 flex items-end">
                                    <button
                                        class="px-3 py-2"
                                        @click="removeAsset(index)"
                                    >
                                        <img
                                            class="w-5"
                                            src="../../../img/trash-icon.svg"
                                            alt="delete"
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button class="underline" @click="addAssetOwner">
                        + Add asset owner
                    </button>
                </div>

                <div class="text-xl font-bold mb-4">
                    Licensing rights information
                </div>

                <FormSelect
                    v-model="form.copyright_usage"
                    id="copyright_usage"
                    label="Usage *"
                    :selectOptions="licenseRightsList"
                    placeholderText="Select usage"
                    containerClass="mb-6"
                    :error="
                        errors.copyright_usage ? errors.copyright_usage[0] : ''
                    "
                />

                <FormTextLabel
                    v-model="form.asset_provider"
                    id="asset_provider"
                    label="Asset Provider *"
                    placeholderText="Add asset provider"
                    containerClass="mb-6"
                    :error="
                        errors.asset_provider ? errors.asset_provider[0] : ''
                    "
                />

                <FormSelect
                    v-model="form.copyright_area"
                    id="copyright_area"
                    label="Copyright area *"
                    :selectOptions="areaList"
                    placeholderText="Select copyright area"
                    containerClass="mb-6"
                    :error="
                        errors.copyright_area ? errors.copyright_area[0] : ''
                    "
                />

                <div class="mb-6">
                    <FormRadio
                        v-model="form.has_expiry"
                        id="has_expiry"
                        label="Copyright expires (optional)"
                        :radioList="hasExpiryRadioList"
                    />
                    <div v-if="form.has_expiry === 'expiry-yes'" class="mt-1">
                        <FormDatepicker
                            v-model="form.copyright_expires_at"
                            id="copyright_expires_at"
                            placeholderText="Select date"
                        />
                    </div>

                    <div
                        v-if="errors.has_expiry"
                        class="mt-1 text-sm text-red-400"
                    >
                        {{ errors.has_expiry[0] }}
                    </div>
                    <div
                        v-if="errors.copyright_expires_at"
                        class="mt-1 text-sm text-red-400"
                    >
                        {{ errors.copyright_expires_at[0] }}
                    </div>
                </div>

                <FormRadio
                    v-model="form.copyright_expire_action"
                    id="copyright_expire_action"
                    label="Action upon copyright expiry *"
                    :radioList="actionUponExpiryRadioList"
                    class="mb-6"
                />

                <FormTextLabel
                    v-model="form.copyright_terms"
                    id="copyright_terms"
                    label="Additional copyright terms (optional)"
                    placeholderText="Add a description"
                    containerClass="mb-6"
                    :error="
                        errors.copyright_terms ? errors.copyright_terms[0] : ''
                    "
                />

                <div class="text-xl font-bold mb-4">Publishing</div>

                <div class="mb-6">
                    <FormRadio
                        v-model="form.publish_option"
                        id="publish_option"
                        label="Publish options *"
                        :radioList="publishOptionRadioList"
                    />
                    <div v-if="form.publish_option === 'publish'" class="mt-1">
                        <FormDatepicker
                            v-model="form.publish_at"
                            id="publish_at"
                            placeholderText="Select date"
                        />
                    </div>
                    <div
                        v-if="errors.publish_at"
                        class="mt-1 text-sm text-red-400"
                    >
                        {{ errors.publish_at[0] }}
                    </div>
                </div>

                <div class="mb-6">
                    <FormRadio
                        v-model="form.unpublish_option"
                        id="unpublish_option"
                        label="Date and time to unpublish asset (optional)"
                        :radioList="unpublishOptionRadioList"
                    />
                    <div
                        v-if="form.unpublish_option === 'unpublish'"
                        class="mt-1"
                    >
                        <FormDatepicker
                            v-model="form.unpublish_at"
                            id="unpublish_at"
                            placeholderText="Select date"
                        />
                    </div>

                    <div
                        v-if="errors.unpublish_at"
                        class="mt-1 text-sm text-red-400"
                    >
                        {{ errors.unpublish_at[0] }}
                    </div>
                </div>

                <div class="mt-8 flex items-center space-x-4">
                    <DeleteAssetLink :asset="asset" />

                    <button type="button" class="underline" @click="cancel()">
                        Cancel
                    </button>

                    <button
                        class="
                            bg-blue-500
                            hover:bg-blue-700
                            text-white
                            font-bold
                            py-2
                            px-4
                        "
                        @click="save()"
                    >
                        Save
                    </button>
                    <div class="text-green-700" v-if="saveSuccessMessage">
                        {{ saveSuccessMessage }}
                    </div>
                    <div class="text-red-400" v-if="saveErrorMessage">
                        {{ saveErrorMessage }}
                    </div>
                    <div class="text-gray-400" v-if="isLoading">
                        Submitting...
                    </div>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import Multiselect from "@vueform/multiselect";
import flatPickr from "vue-flatpickr-component";
import FormTextLabel from "@/components/FormTextLabel";
import FormTextAreaLabel from "@/components/FormTextAreaLabel";
import FormSelect from "@/components/FormSelect";
import FormRadio from "@/components/FormRadio";
import FormDatepicker from "@/components/FormDatepicker";
import ManageCateoryModal from "@/Pages/Asset/ManageCateoryModal";
import DeleteAssetLink from "@/Pages/Asset/DeleteAssetLink";
import "flatpickr/dist/flatpickr.css";

export default {
    props: ["asset"],
    components: {
        AppLayout,
        Multiselect,
        flatPickr,
        FormTextLabel,
        FormTextAreaLabel,
        FormSelect,
        FormRadio,
        FormDatepicker,
        ManageCateoryModal,
        DeleteAssetLink,
    },
    data() {
        return {
            datePickerConfig: {
                enableTime: true,
                altFormat: "d F Y, h:i K",
                altInput: true,
            },
            categoryList: [],
            actionUponExpiryRadioList: [
                {
                    key: "expire",
                    name: "Move to Expired tab",
                },
                {
                    key: "delete",
                    name: "Delete asset (Asset can be recovered within 30 days upon deletion)",
                },
            ],
            unpublishOptionRadioList: [
                {
                    key: "none",
                    name: "No unpublish date",
                },
                {
                    key: "unpublish",
                    name: "Select date and time of unpublish",
                },
            ],
            publishOptionRadioList: [
                {
                    key: "draft",
                    name: "Draft",
                },
                {
                    key: "publish",
                    name: "Select date and time of publish",
                },
            ],
            hasExpiryRadioList: [
                {
                    key: "expiry-none",
                    name: "No Expiry date",
                },
                {
                    key: "expiry-yes",
                    name: "Select date and time of expiry",
                },
            ],
            areaList: [
                {
                    key: "regional",
                    name: "Regional",
                },
                {
                    key: "global",
                    name: "Global",
                },
            ],
            licenseRightsList: [
                {
                    key: "digital-editorial",
                    name: "Digital and editorial use",
                },
                {
                    key: "digital",
                    name: "Digital use only",
                },
                {
                    key: "editorial",
                    name: "Editorial use only",
                },
            ],
            form: {},
            formImage: null,
            previewImageUrl: "",
            categoryModalVisible: false,
            categoriesMessage: "",
            saveSuccessMessage: "",
            saveErrorMessage: "",
            isLoading: false,
            imageErrors: {},
            errors: {},
        };
    },
    watch: {},
    methods: {
        async save() {
            this.isLoading = true;

            if (this.formImage) {
                var formData = new FormData();
                formData.append("images", this.$refs.file.files[0]);

                await axios.post(
                    `/api/assets-upload-photo/${this.asset.id}`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );
            }

            await axios
                .post(`/api/assets/${this.asset.id}`, this.form)
                .then((response) => {
                    // this.savedSuccess();
                    // this.errors = {};
                    window.close();
                })
                .catch((error) => {
                    this.savedError();
                    if (error.response) {
                        if (error.response.status === 422) {
                            this.errors = error.response.data.errors;
                        }
                    } else if (error.request) {
                        // console.log(error.request);
                    } else {
                        // console.log("Error", error.message);
                    }
                });

            this.isLoading = false;
        },
        addAssetOwner() {
            if (!this.form.asset_owners || this.form.asset_owners.length < 1) {
                this.form.asset_owners = [
                    {
                        name: "",
                        email: "",
                    },
                ];
                return;
            }

            this.form.asset_owners.push({
                name: "",
                email: "",
            });
        },
        removeAsset(index) {
            this.form.asset_owners.splice(index, 1);
        },
        categoryModalShow(visible) {
            this.categoryModalVisible = visible;
        },
        savedSuccess() {
            this.saveSuccessMessage = "Saved!";
            setTimeout(() => {
                this.saveSuccessMessage = "";
            }, 3000);
        },
        savedError() {
            this.saveErrorMessage = "Error! Please check the form for errors.";
            setTimeout(() => {
                this.saveErrorMessage = "";
            }, 3000);
        },
        closeCategoryModal(visible, showMessage) {
            this.updateCategories();
            this.categoryModalShow(visible);

            if (showMessage) {
                this.categoriesMessage = showMessage;

                setTimeout(() => {
                    this.categoriesMessage = "";
                }, 3000);
            }
        },
        updateCategories() {
            axios.get(`/api/categories`).then((response) => {
                let currentCategories = this.form.categories;

                let cleanUpCategories = currentCategories.filter((category) => {
                    return !!response.data.data.find((newCatgory) => {
                        return newCatgory.id === category.id;
                    });
                });

                this.form.categories = cleanUpCategories;

                this.categoryList = response.data.data;
            });
        },
        onFileReplace() {
            var formData = new FormData();
            formData.append("images", this.$refs.file.files[0]);

            axios
                .post("/api/assets-upload-photo/validate", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then((response) => {
                    let self = this;
                    var reader = new FileReader();
                    reader.onload = function (e) {
                        self.previewImageUrl = e.target.result;
                    };
                    reader.readAsDataURL(this.$refs.file.files[0]);
                    self.formImage = this.$refs.file.files[0];
                })
                .catch((error) => {
                    if (error.response) {
                        if (error.response.status === 422) {
                            this.imageErrors = error.response.data.errors;
                        }
                    } else if (error.request) {
                        // console.log(error.request);
                    } else {
                        // console.log("Error", error.message);
                    }
                });
        },
        cancel() {
            window.close();
        },
    },
    mounted() {
        this.updateCategories();

        this.form.type = this.asset.type;
        this.form.title = this.asset.title;
        this.form.description = this.asset.description;
        this.form.keywords = this.asset.keywords;
        this.form.categories = this.asset.categories.data;
        this.form.asset_owners = this.asset.asset_owners;
        this.form.copyright_usage = this.asset.copyright_usage;
        this.form.asset_provider = this.asset.asset_provider
            ? this.asset.asset_provider
            : "Toyota";
        this.form.copyright_area = this.asset.copyright_area;
        this.form.copyright_expires_at = this.asset.copyright_expires_at_form;
        this.form.copyright_expire_action = this.asset.copyright_expire_action
            ? this.asset.copyright_expire_action
            : "expire";
        this.form.copyright_terms = this.asset.copyright_terms;
        this.form.publish_at = this.asset.publish_at_form;
        this.form.unpublish_at = this.asset.unpublish_at_form;

        // set all the radio buttons options
        this.form.has_expiry = this.asset.copyright_expires_at
            ? "expiry-yes"
            : "expiry-none";

        this.form.publish_option = this.asset.publish_at ? "publish" : "draft";

        this.form.unpublish_option = this.asset.unpublish_at
            ? "unpublish"
            : "none";

        this.previewImageUrl = this.asset.images.data.find(
            (image) => image.size_type === "preview"
        )?.file_url;
    },
};
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style scoped>
.multiselect-gray {
    --ms-tag-bg: #ccc;
    --ms-tag-color: #000;
    --ms-tag-radius: 0;
}
</style>
